import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TimelineEvent } from 'types/timeline';
import type { NotificationRecord } from '../../NotificationCard/NotificationCardMolecule';
import SphereTimelineEventMolecule from '../../SphereTimelineEvent/SphereTimelineEventMolecule';
import { FeedTaskStackMolecule } from '../../FeedTaskStack';
import { FeedNotificationStackMolecule } from '../../FeedNotificationStack';
import ChatMessageCardAtom from '../../../atoms/ChatMessageCard/ChatMessageCardAtom';
import { useConversation, type ApiMessage } from '../../SphereChatPanel/UseConversation';
import type { StatusValue } from '../../StatusCheckInDialog/CheckInFormContent';
import { Task } from '../../TaskList/TaskListMolecule.types';
import { useToolbarContextOrNull } from '../../../../context/ToolbarContext';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { useMetaspaceEvents } from '../../../../hooks/useMetaspaceEvents';
import { FeedFilterButtonMolecule } from '../../FeedFilterButton';
import { resetFilterState, isDefaultFilterState } from '../../../../config/feedFilterConfig';
import './HomeMode.scss';

/**
 * Discriminated union for activity stream items (Zone 3)
 */
type ActivityStreamItem =
  | { type: 'timeline'; id: string; timestamp: string; data: TimelineEvent }
  | { type: 'conversation'; id: string; timestamp: string; data: ApiMessage };

export interface HomeModeProps {
  /** Active sphere ID for zone localStorage keys */
  activeSphereId: string;
  /** Additional sphere IDs (orbitals) - future enhancement */
  additionalSphereIds?: string[];
  /** Callback when a notification is clicked */
  onNotificationClick?: (notification: NotificationRecord) => void;
  /** Callback when a task is clicked */
  onTaskClick?: (task: Task) => void;
  /** Callback when check-in action is triggered */
  onCheckIn?: (task: Task) => void;
  /** Callback when a timeline event is clicked */
  onTimelineEventClick?: (event: TimelineEvent) => void;
  /** Callback when a conversation message is clicked (navigates to Chat tab) */
  onConversationClick?: (message: ApiMessage) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * HomeMode - Three-zone feed layout:
 * Zone 1: Urgent/overdue task stack (FeedTaskStackMolecule)
 * Zone 2: Urgent notification stack (FeedNotificationStackMolecule)
 * Zone 3: Chronological activity stream (timeline events + conversation cards)
 */
const HomeMode: React.FC<HomeModeProps> = ({
  activeSphereId,
  additionalSphereIds: _additionalSphereIds,
  onNotificationClick,
  onTaskClick,
  onCheckIn,
  onTimelineEventClick,
  onConversationClick,
  className = '',
}) => {
  const { t } = useTranslation();

  const toolbarContext = useToolbarContextOrNull();
  const apiClients = useApiClientsOrNull();
  const { emitConversationMessageClicked } = useMetaspaceEvents({ subscriberId: 'home-mode' });

  const feedFilters = toolbarContext?.feedFilters;
  const setFeedFilters = toolbarContext?.setFeedFilters;

  const noopAsync = async (): Promise<void> => { /* no-op */ };

  // Track which task is being checked in (for API call in handleCheckInSubmit)
  const checkInTaskRef = useRef<Task | null>(null);

  const currentUserId = toolbarContext?.currentUserId ?? undefined;

  // Get UNREAD notifications from shared context
  const notifications = toolbarContext?.unreadNotifications ?? [];
  const notificationsLoading = toolbarContext?.notificationState.isLoading ?? false;
  const notificationsError = toolbarContext?.notificationState.error ?? null;
  const markAsRead = toolbarContext?.markNotificationAsRead ?? noopAsync;

  // Get tasks from shared context
  const tasks = toolbarContext?.taskState.tasks ?? [];
  const tasksLoading = toolbarContext?.taskState.isLoading ?? false;
  const tasksError = toolbarContext?.taskState.error ?? null;

  // Get timeline events from shared context
  const timelineEvents = toolbarContext?.timelineState.events ?? [];
  const timelineLoading = toolbarContext?.timelineState.isLoading ?? false;
  const timelineError = toolbarContext?.timelineState.error ?? null;
  const timelineHasMore = toolbarContext?.timelineState.hasMore ?? false;
  const fetchMoreTimeline = toolbarContext?.fetchMoreTimeline;

  // Fetch conversation messages via the thread API (sphere-scoped)
  const conversationContextKey = activeSphereId ? `sphere:${activeSphereId}` : undefined;
  const { messages: conversationMessages, loading: conversationLoading } = useConversation(conversationContextKey, true);

  // Ref for scroll container to detect scroll-to-bottom
  const feedRef = useRef<HTMLDivElement>(null);

  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pullStartY = useRef<number | null>(null);
  const PULL_THRESHOLD = 60;

  // Sentinel ref for infinite scroll
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Build activity stream: timeline events + conversation messages sorted by timestamp,
  // with client-side filtering for conversations and perspective direction subtypes.
  const activityStreamItems = useMemo<ActivityStreamItem[]>(() => {
    const items: ActivityStreamItem[] = [];

    for (const event of timelineEvents) {
      // Client-side perspective direction filtering
      if (
        feedFilters &&
        (event.eventType === 'perspective_assigned' || event.eventType === 'perspective_unassigned')
      ) {
        const isOutbound = !!event.payload?.targetSphereId;
        const subtypeKey = `${event.eventType}:${isOutbound ? 'outbound' : 'inbound'}`;
        if (feedFilters.disabledSubtypes.includes(subtypeKey)) {
          continue;
        }
      }

      items.push({
        type: 'timeline',
        id: `timeline-${event.id}`,
        timestamp: event.timestamp,
        data: event,
      });
    }

    // Only include conversation messages if conversations are enabled
    if (!feedFilters || feedFilters.conversationsEnabled) {
      for (const msg of conversationMessages) {
        items.push({
          type: 'conversation',
          id: `conv-msg-${msg.message_id}`,
          timestamp: msg.created_at,
          data: msg,
        });
      }
    }

    items.sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      if (Number.isNaN(dateB)) return -1;
      if (Number.isNaN(dateA)) return 1;
      return dateB - dateA;
    });

    return items;
  }, [timelineEvents, conversationMessages, feedFilters]);


  const handleNotificationClick = useCallback(
    (notification: NotificationRecord) => {
      if (notification.status !== 'read') {
        markAsRead(notification.id);
      }
      onNotificationClick?.(notification);
    },
    [markAsRead, onNotificationClick]
  );

  const handleTaskClick = useCallback(
    (task: Task) => {
      onTaskClick?.(task);
    },
    [onTaskClick]
  );

  // Store the task being checked in so handleCheckInSubmit knows which task
  const handleTaskCheckIn = useCallback(
    (task: Task) => {
      checkInTaskRef.current = task;
      onCheckIn?.(task);
    },
    [onCheckIn]
  );

  // Handle inline check-in form submission from FeedTaskStackMolecule
  const handleCheckInSubmit = useCallback(
    async (statusValue: StatusValue, comment?: string) => {
      const task = checkInTaskRef.current;
      if (!apiClients?.graphClient || !task?.factorId || !task?.sphereId) return;

      try {
        await apiClients.graphClient.post(
          `/sphere/${task.sphereId}/factors/${task.factorId}/checkin`,
          { statusValue, comment }
        );
        await toolbarContext?.refreshTasks();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('[HomeMode] Check-in failed:', message);
      }
    },
    [apiClients?.graphClient, toolbarContext]
  );

  const handleConversationClick = useCallback(
    (message: ApiMessage) => {
      if (conversationContextKey) {
        emitConversationMessageClicked(message.message_id, message.thread_id, conversationContextKey);
      }
      onConversationClick?.(message);
    },
    [onConversationClick, emitConversationMessageClicked, conversationContextKey]
  );

  const handleTimelineEventClick = useCallback(
    (event: TimelineEvent) => {
      onTimelineEventClick?.(event);
    },
    [onTimelineEventClick]
  );

  // Auto-fetch: ResizeObserver detects when content doesn't fill the feed container
  useEffect(() => {
    const el = feedRef.current;
    if (!el) return;

    const checkAndFetch = () => {
      if (el.scrollHeight <= el.clientHeight) {
        if (timelineHasMore && !timelineLoading && fetchMoreTimeline) fetchMoreTimeline();
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      checkAndFetch();
    });

    resizeObserver.observe(el);
    checkAndFetch();

    return () => resizeObserver.disconnect();
  }, [timelineLoading, timelineHasMore, fetchMoreTimeline]);

  // Infinite scroll: sentinel IntersectionObserver triggers fetch when scrolled near bottom
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = feedRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (timelineHasMore && !timelineLoading && fetchMoreTimeline) fetchMoreTimeline();
        }
      },
      { root: container, rootMargin: '100px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [timelineLoading, timelineHasMore, fetchMoreTimeline]);

  // Pull-to-refresh: trigger all data refreshes
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await Promise.all([
        toolbarContext?.refreshNotifications(),
        toolbarContext?.refreshTasks(),
        toolbarContext?.refreshTimeline(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, toolbarContext]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const el = feedRef.current;
    if (!el || isRefreshing) return;
    if (el.scrollTop <= 0) {
      pullStartY.current = e.touches[0].clientY;
    }
  }, [isRefreshing]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (pullStartY.current === null || isRefreshing) return;
    const el = feedRef.current;
    if (!el || el.scrollTop > 0) {
      pullStartY.current = null;
      setPullDistance(0);
      return;
    }
    const delta = e.touches[0].clientY - pullStartY.current;
    if (delta > 0) {
      // Dampen the pull distance for a rubber-band feel
      setPullDistance(Math.min(delta * 0.5, PULL_THRESHOLD * 2));
    }
  }, [isRefreshing, PULL_THRESHOLD]);

  const handleTouchEnd = useCallback(() => {
    if (pullStartY.current === null) return;
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      handleRefresh();
    }
    pullStartY.current = null;
    setPullDistance(0);
  }, [pullDistance, PULL_THRESHOLD, isRefreshing, handleRefresh]);

  const isLoading = notificationsLoading || tasksLoading || timelineLoading || conversationLoading;
  const error = notificationsError || tasksError || timelineError;
  const hasNoData = notifications.length === 0 && tasks.length === 0 && timelineEvents.length === 0 && conversationMessages.length === 0;
  const filtersActive = feedFilters ? !isDefaultFilterState(feedFilters) : false;
  const allFilteredOut = activityStreamItems.length === 0 && !hasNoData && filtersActive;

  const handleResetFilters = useCallback(() => {
    setFeedFilters?.(resetFilterState());
  }, [setFeedFilters]);

  // Loading state
  if (isLoading && hasNoData) {
    return (
      <div className={`home-mode home-mode--loading ${className}`}>
        <div className="home-mode__loading">
          <i className="pi pi-spin pi-spinner" />
          <span>{t('notifications.loading')}</span>
        </div>
      </div>
    );
  }

  // Error state (only show if no data at all)
  if (error && hasNoData) {
    return (
      <div className={`home-mode home-mode--error ${className}`}>
        <div className="home-mode__error">
          <i className="pi pi-exclamation-circle" />
          <span>{t(error)}</span>
        </div>
      </div>
    );
  }

  const pullIconClass = (() => {
    if (isRefreshing) return 'pi-spin pi-spinner';
    if (pullDistance >= PULL_THRESHOLD) return 'pi-arrow-up';
    return 'pi-arrow-down';
  })();

  return (
    <div className={`home-mode ${className}`}>
      {/* Panel title */}
      <div className="home-mode__title-bar">
        <i className="pi pi-home" />
        <span>{t('toolbar.activity')}</span>
      </div>

      {/* Scrollable feed with three zones */}
      <div
        className="home-mode__feed"
        ref={feedRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull-to-refresh indicator */}
        {(pullDistance > 0 || isRefreshing) && (
          <div
            className={`home-mode__pull-indicator${isRefreshing ? ' home-mode__pull-indicator--refreshing' : ''}`}
            style={isRefreshing ? undefined : { height: `${pullDistance}px` }}
          >
            <i className={`pi ${pullIconClass}`} />
            {!isRefreshing && pullDistance >= PULL_THRESHOLD && (
              <span>{t('feed.pullToRefresh.release')}</span>
            )}
            {isRefreshing && (
              <span>{t('feed.pullToRefresh.refreshing')}</span>
            )}
          </div>
        )}
        {/* Zone 1: Urgent/Overdue Task Stack — auto-hides when empty */}
        {currentUserId && (
          <FeedTaskStackMolecule
            tasks={tasks}
            currentUserId={currentUserId}
            sphereId={activeSphereId}
            onTaskClick={handleTaskClick}
            onCheckIn={handleTaskCheckIn}
            onCheckInSubmit={handleCheckInSubmit}
          />
        )}

        {/* Zone 2: Urgent Notification Stack — auto-hides when empty */}
        <FeedNotificationStackMolecule
          notifications={notifications}
          sphereId={activeSphereId}
          onNotificationClick={handleNotificationClick}
          onMarkRead={markAsRead}
        />

        {/* Divider between stacks and activity stream */}
        {(tasks.length > 0 || notifications.length > 0) && activityStreamItems.length > 0 && (
          <div className="home-mode__divider" />
        )}

        {/* Feed header: activity title + filter button */}
        {feedFilters && setFeedFilters && (
          <div className="home-mode__feed-header">
            <span className="home-mode__feed-title">{t('feed.activityStream.title')}</span>
            <FeedFilterButtonMolecule
              filters={feedFilters}
              onFiltersChange={setFeedFilters}
            />
          </div>
        )}

        {/* Zone 3: Chronological Activity Stream */}
        {allFilteredOut && !isLoading && (
          <div className="home-mode__feed-empty home-mode__feed-empty--filtered">
            <i className="pi pi-filter-slash" />
            <span>{t('feed.filters.noResults')}</span>
            <button
              className="home-mode__reset-filters"
              onClick={handleResetFilters}
              type="button"
            >
              <i className="pi pi-refresh" />
              <span>{t('feed.filters.reset')}</span>
            </button>
          </div>
        )}

        {activityStreamItems.length === 0 && !allFilteredOut && !isLoading && (
          <div className="home-mode__feed-empty">
            <i className="pi pi-check-circle" />
            <span>{t('feed.activityStream.empty')}</span>
          </div>
        )}

        {activityStreamItems.map((item) => (
          <div key={item.id} className="home-mode__card-wrapper">
            {item.type === 'timeline' ? (
              <SphereTimelineEventMolecule
                event={item.data}
                onClick={handleTimelineEventClick}
              />
            ) : (
              <ChatMessageCardAtom
                message={item.data}
                onClick={handleConversationClick}
              />
            )}
          </div>
        ))}

        {/* Loading indicator for infinite scroll */}
        {timelineLoading && activityStreamItems.length > 0 && (
          <div className="home-mode__feed-loading-more">
            <i className="pi pi-spin pi-spinner" />
          </div>
        )}

        {/* Sentinel for infinite scroll detection */}
        <div ref={sentinelRef} className="home-mode__scroll-sentinel" />
      </div>
    </div>
  );
};

export default HomeMode;
